/**
 * 画布右上角工具条：全屏、导出、主题切换
 */
import { IconDownload, IconPlayerPlay, IconMoon, IconSun } from '@tabler/icons-react';
import { Button, Dropdown, Label, Toolbar, cn } from '@heroui/react';
import { useTheme } from '@/hooks/useTheme';
import { useWorkspaceChrome } from '@/components/canvas/workspace/stage/WorkspaceChromeContext';

export interface TopRightToolbarProps {
  onExportPng: () => void;
  onExportJpeg: () => void;
  isExporting?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export function TopRightToolbar({
  onExportPng,
  onExportJpeg,
  isExporting = false,
  className,
  style,
}: TopRightToolbarProps) {
  const chrome = useWorkspaceChrome();
  const { resolvedTheme, setTheme } = useTheme();
  const isLight = (resolvedTheme ?? 'light') === 'light';
  const isFullscreen = chrome?.isFullscreen ?? false;
  const canTogglePresentation = isFullscreen || (chrome?.canEnterFullscreen ?? false);

  return (
    <div
      className={cn('absolute right-2 top-2 z-[50]', className)}
      style={style}
    >
      <Toolbar
        aria-label="视图、导出与主题"
        className="px-2 py-1 shadow-none rounded-4xl bg-transparent"
      >
          {chrome?.onToggleFullscreen ? (
            <Button
              variant="tertiary"
              size="sm"
              onPress={chrome.onToggleFullscreen}
              isDisabled={!canTogglePresentation}
              aria-label={isFullscreen ? '退出演示' : '演示模式'}
            >
              <IconPlayerPlay stroke={1.25} />
                {isFullscreen ? '退出演示' : '演示'}
            </Button>
          ) : null}

          <Dropdown>
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              className="shrink-0 gap-1.5"
              isDisabled={isExporting}
              aria-label="导出"
            >
              <IconDownload className="size-4 shrink-0" stroke={1.25} />
              <span className="whitespace-nowrap text-sm">导出</span>
            </Button>
            <Dropdown.Popover className="min-w-36" placement="bottom end" offset={6}>
              <Dropdown.Menu
                aria-label="导出格式"
                onAction={(key) => {
                  if (key === 'png') onExportPng();
                  if (key === 'jpeg') onExportJpeg();
                }}
              >
                <Dropdown.Item id="png" textValue="导出 PNG">
                  <Label className="font-normal">导出 PNG</Label>
                </Dropdown.Item>
                <Dropdown.Item id="jpeg" textValue="导出 JPG">
                  <Label className="font-normal">导出 JPG</Label>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown>

          <div
            className="flex items-center rounded-full bg-default"
            role="group"
            aria-label="主题切换"
          >
            <Button
              variant={isLight ? 'primary' : 'ghost'}
              size="sm"
              isIconOnly
              aria-label="浅色主题"
              aria-pressed={isLight}
              onPress={() => setTheme('light')}
            >
              <IconSun className="size-4" stroke={1.25} />
            </Button>
            <Button
              variant={!isLight ? 'primary' : 'ghost'}
              size="sm"
              isIconOnly
              aria-label="深色主题"
              aria-pressed={!isLight}
              onPress={() => setTheme('dark')}
            >
              <IconMoon className="size-4" stroke={1.25} />
            </Button>
          </div>
      </Toolbar>
    </div>
  );
}
